import { useEffect } from 'react';
import { Layout, CtaForm } from '../../../components';
import Chart from 'chart.js/auto';

const CHART_PRIMARY_COLOR = '#2ecc71';
const CHART_ELEMENT_OPTION = {
	point: {
		backgroundColor: CHART_PRIMARY_COLOR,
		borderColor: CHART_PRIMARY_COLOR,
		hitRadius: 2,
		hoverBorderWidth: 2,
	},
	line: {
		borderColor: CHART_PRIMARY_COLOR,
	},
};
const UPTIMEROBOT_URL = process.env.NEXT_PUBLIC_UPTIMEROBOT_URL;

export default function LiveSupportStatus({ responseTimeData, uptimeData }) {
	const rollingAvgResponseTime = responseTimeData?.slice(-1)?.pop()?.rolling_avg_response_time || 0;

	const totalUptime = uptimeData?.reduce((sum, dateUptime) => sum + dateUptime.uptime, 0) || 0;
	const avgUptime = (Math.floor((totalUptime / (uptimeData?.length || 0)) * 100) / 100) || 0;
	const last30daysUptime = uptimeData?.slice(Math.max((uptimeData?.length || 0) - 30, 0)) || [];

	useEffect(() => {
		let responseTimeChart = new Chart(document.getElementById('responseTime'), {
			type: 'line',
			data: {
				labels: responseTimeData.map((row) => row.date.slice(0, 5)),
				datasets: [
					{
						label: 'Response Time',
						data: responseTimeData.map((row) => row.rolling_avg_response_time),
					},
				],
			},
			options: {
				elements: CHART_ELEMENT_OPTION,
				scales: {
					y: {
						min: 0,
						// max: 60,
						ticks: {
							callback: function (value, index, ticks) {
								return value + ' minutes';
							},
						},
					},
				},
				plugins: {
					legend: {
						position: 'bottom',
						onClick: () => {},
					},
					tooltip: {
						callbacks: {
							label: (item) => `Response time: ${item.formattedValue} minutes`,
						},
					},
				},
			},
		});

		let uptimeChart = new Chart(document.getElementById('uptime'), {
			type: 'line',
			data: {
				labels: last30daysUptime.map((row) => row.date.slice(0, 5)),
				datasets: [
					{
						label: 'Uptime',
						data: last30daysUptime.map((row) => row.uptime),
					},
				],
			},
			options: {
				elements: CHART_ELEMENT_OPTION,
				scales: {
					y: {
						min: 0,
						max: 100,
						ticks: {
							callback: function (value, index, ticks) {
								return value + '%';
							},
						},
					},
				},
				plugins: {
					legend: {
						position: 'bottom',
						onClick: () => {},
					},
					tooltip: {
						callbacks: {
							label: (item) => `Uptime: ${item.formattedValue}%`,
						},
					},
				},
			},
		});
	}, []);

	return (
		<Layout pageName="Live Support Status">
			<div className="support-status-page">
				<section className="heading-container">
					<div className="container">
						<div className="heading">
							<h1 className="heading__title">Live Support Status</h1>
							<h2 className="heading__desc">
								We are proud of our uptime and support responsiveness. See below for real metrics updated daily.
							</h2>
						</div>
					</div>
				</section>

				<section className="status-chart">
					<h1 className="status-chart__title">
						Average Initial Response Time to a Chat message (2 week rolling average)
					</h1>
					<h1 className="status-chart__data">{rollingAvgResponseTime} minutes</h1>
					<div className="response-time-chart">
						<canvas id="responseTime"></canvas>
					</div>
				</section>

				<section className="status-chart">
					<h1 className="status-chart__title">System Uptime (Annual rolling average)</h1>
					<h1 className="status-chart__data">{avgUptime}%</h1>
					<div className="uptime-chart">
						<canvas id="uptime"></canvas>
					</div>
					<div className="uptime-stats-link">
						<span>Monitored with</span>
						<a href={UPTIMEROBOT_URL} target="_blank" rel="noreferrer">
							UptimeRobot
						</a>
					</div>
				</section>

				<CtaForm />
			</div>
		</Layout>
	);
}

export async function getServerSideProps() {
	// Temporarily disable the page:
	// return {
	// 	notFound: true,
	// };

	try {
		const [responseTimeResponse, uptimeResponse] = await Promise.all([
			fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/api/support-response-time`),
			fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/api/system-uptime`),
		]);
		const [responseTime, uptime] = await Promise.all([
			responseTimeResponse.json(),
			uptimeResponse.json(),
		]);
		
		const responseTimeData = responseTime.data;
		const uptimeData = uptime.data.reverse();

		return {
			props: {
				responseTimeData,
				uptimeData,
			},
		};
	} catch (err) {
		console.error('🚀 ~ page: live support status ~ getServerSideProps ~ err', err);

		return {
			props: {
				responseTimeData: [],
				uptimeData: [],
			},
		};
	}
}
